/**
 * LifecycleTimeline — render horizontal de las phases del lifecycle.
 *
 * Cada phase es un nodo circular con número, debajo va el nombre,
 * el status badge y un counter de artefactos. Los conectores entre
 * phases reflejan el progreso (lleno=approved, parcial=in_progress).
 *
 * Click en una phase la marca como seleccionada (manejado por el
 * orchestrator). La phase activa (in_progress) tiene un anillo
 * `ring` para destacarse visualmente.
 *
 * Mapeo de colores del círculo (matchea la leyenda — ProjectLifecycleView):
 *   approved     → bg-official (verde)
 *   in_progress  → bg-review (naranja) con ring — el backend marca esto
 *                  automáticamente al primer save de cualquier draft de
 *                  la phase (sprint 3.6 de B.6.d.10).
 *   blocked      → bg-critical (rojo)
 *   pending/not_started → bg-ink-subtle (gris relleno) — NO outline
 *   skipped      → comprimido: círculo chico + opacity reducida + sin
 *                  nombre/badge/counter (palanca de upsell visible pero
 *                  sin ahogar el foco del proyecto contratado).
 */

import { Fragment } from 'react'
import { useTranslation } from 'react-i18next'

import type { ProjectPhase } from '../../api/projects'
import { PhaseStatusBadge } from './PhaseStatusBadge'

interface LifecycleTimelineProps {
  phases: ProjectPhase[]
  /** phase_code de la phase actualmente seleccionada (drill-in). */
  selectedCode: string | null
  onSelect: (phase: ProjectPhase) => void
}

export function LifecycleTimeline({
  phases,
  selectedCode,
  onSelect,
}: LifecycleTimelineProps) {
  // El backend ya devuelve ordered por phase_order; defensivo igualmente.
  const ordered = [...phases].sort((a, b) => a.phase_order - b.phase_order)

  return (
    <div className="bg-surface border border-line rounded-lg p-8 overflow-x-auto">
      <div className="flex items-start min-w-max gap-0">
        {ordered.map((phase, i) => {
          const isLast = i === ordered.length - 1
          const next = isLast ? null : ordered[i + 1]
          return (
            <Fragment key={phase.id}>
              <PhaseNode
                phase={phase}
                selected={selectedCode === phase.phase_code}
                onClick={() => onSelect(phase)}
              />
              {!isLast && next ? (
                <Connector
                  fromStatus={phase.status}
                  toStatus={next.status}
                  fromContractMode={phase.contract_mode}
                  toContractMode={next.contract_mode}
                />
              ) : null}
            </Fragment>
          )
        })}
      </div>
    </div>
  )
}

// ─── Phase node ─────────────────────────────────────────────────────────────
function PhaseNode({
  phase,
  selected,
  onClick,
}: {
  phase: ProjectPhase
  selected: boolean
  onClick: () => void
}) {
  const { t } = useTranslation('projects')
  // El backend resuelve el nombre del lifecycle correcto + i18n.
  // Caemos al phase_code solo si el backend no devuelve nada (legacy).
  const phaseName = phase.name || phase.phase_code

  const isSkipped = phase.contract_mode === 'skipped'
  const isClientProvided = phase.contract_mode === 'external_canon'
  const isApproved = phase.status === 'approved'
  const isInProgress = phase.status === 'in_progress'
  const isBlocked = phase.status === 'blocked'

  // ─── Skipped: render comprimido ──────────────────────────────────────────
  // Decisión de producto: las phases not contracted siguen visibles
  // (catálogo HSL → palanca de upsell del PM con el cliente) pero NO
  // ahogan el foco del proyecto contratado. Sin nombre, badge, counter
  // ni ancho fijo. Tooltip nativo con el nombre.
  if (isSkipped) {
    const skippedWrapper = selected
      ? 'flex flex-col items-center w-14 shrink-0 px-1 py-2 rounded-md bg-brand-soft/30 border border-brand/30 cursor-pointer opacity-60 hover:opacity-100 transition-opacity'
      : 'flex flex-col items-center w-14 shrink-0 px-1 py-2 cursor-pointer opacity-50 hover:opacity-100 transition-opacity'
    return (
      <button
        type="button"
        onClick={onClick}
        aria-pressed={selected}
        title={`${phaseName} · ${t('lifecycle.legend.skipped')}`}
        className={skippedWrapper}
      >
        {/* Slot de altura igual al círculo activo (h-10) para que los
            conectores se alineen visualmente sin importar el tamaño
            real del círculo skipped. */}
        <div className="h-10 grid place-items-center">
          <div className="w-7 h-7 rounded-full bg-surface-muted border border-line text-ink-subtle grid place-items-center font-display text-[10px] font-semibold shrink-0">
            {phase.phase_order}
          </div>
        </div>
      </button>
    )
  }

  // ─── Phases activas (todos los demás contract_modes) ─────────────────────
  // Color del círculo principal — matchea la leyenda (ProjectLifecycleView):
  //  - external_canon (literal API; UI: client-provided) NO aprobada → muted con borde
  //  - approved      → official (verde)
  //  - in_progress   → review (naranja) con ring — el backend lo marca
  //                    automáticamente al primer save de cualquier draft
  //                    de la phase (sprint 3.6 de B.6.d.10).
  //  - blocked       → critical (rojo)
  //  - default       → bg-ink-subtle (gris medio relleno) → matchea legend "Pending"
  const circleClass =
    isClientProvided && !isApproved
      ? 'w-10 h-10 rounded-full bg-surface-muted border-2 border-dashed border-line-strong text-ink-subtle grid place-items-center font-display text-sm font-semibold shrink-0'
      : isApproved
        ? 'w-10 h-10 rounded-full bg-official text-canvas grid place-items-center font-display text-sm font-semibold shrink-0'
        : isInProgress
          ? 'w-10 h-10 rounded-full bg-review text-canvas grid place-items-center font-display text-sm font-semibold ring-4 ring-review-soft shrink-0'
          : isBlocked
            ? 'w-10 h-10 rounded-full bg-critical text-canvas grid place-items-center font-display text-sm font-semibold shrink-0'
            : 'w-10 h-10 rounded-full bg-ink-subtle text-canvas grid place-items-center font-display text-sm font-semibold shrink-0'

  // Selection ring overlay — sólo si está seleccionada y no es la `in_progress` (que ya tiene su propio ring).
  const wrapperClass = selected
    ? 'flex flex-col items-center w-32 shrink-0 rounded-md bg-brand-soft/30 px-2 py-2 -mx-1 border border-brand/30 cursor-pointer'
    : 'flex flex-col items-center w-32 shrink-0 px-2 py-2 -mx-1 cursor-pointer hover:bg-surface-muted/60 rounded-md'

  // Counter de artefactos. client-provided phases (external_canon) no muestra X/Y hasta aprobada.
  const counter = computeCounter(phase)

  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={wrapperClass}
    >
      <div className={circleClass}>{phase.phase_order}</div>
      <p
        className={
          isClientProvided && !isApproved
            ? 'font-display text-xs font-semibold mt-2 text-center text-ink-muted'
            : 'font-display text-xs font-semibold mt-2 text-center text-ink'
        }
      >
        {phaseName}
      </p>
      <div className="mt-1">
        <PhaseStatusBadge
          status={phase.status}
          contractMode={phase.contract_mode}
          size="xs"
        />
      </div>
      <p className="text-[10px] text-ink-subtle mt-1 text-center">{counter}</p>
    </button>
  )
}

// ─── Connector entre phases ────────────────────────────────────────────────
function Connector({
  fromStatus,
  toStatus,
  fromContractMode,
  toContractMode,
}: {
  fromStatus: ProjectPhase['status']
  toStatus: ProjectPhase['status']
  fromContractMode: ProjectPhase['contract_mode']
  toContractMode: ProjectPhase['contract_mode']
}) {
  // Si CUALQUIERA de los lados está skipped, conector muted (las skipped
  // están comprimidas y no deben atraer la mirada).
  // Si origen está approved → conector official (lleno).
  // Si origen o destino está in_progress → conector con gradiente activo.
  const isDone = fromStatus === 'approved'
  const isActive = fromStatus === 'in_progress' || toStatus === 'in_progress'
  const isMuted =
    fromContractMode === 'skipped' ||
    toContractMode === 'skipped' ||
    fromStatus === 'skipped' ||
    toStatus === 'skipped'

  // Conector más fino y corto cuando toca skipped — refuerza la
  // compresión visual del bloque not-contracted.
  const className = isMuted
    ? 'h-px mt-5 flex-1 rounded bg-line/40'
    : isDone
      ? 'h-1 mt-5 flex-1 rounded bg-official'
      : isActive
        ? 'h-1 mt-5 flex-1 rounded bg-gradient-to-r from-official to-line'
        : 'h-1 mt-5 flex-1 rounded bg-line'
  return <div aria-hidden="true" className={className} />
}

// ─── Helpers ────────────────────────────────────────────────────────────────
function computeCounter(phase: ProjectPhase): string {
  if (phase.contract_mode === 'skipped') {
    return ''
  }
  if (
    phase.contract_mode === 'external_canon' &&
    phase.status !== 'approved'
  ) {
    return ''
  }
  const counts = phase.artifact_counts
  const approved = counts.approved ?? 0
  const total =
    (counts.draft ?? 0) +
    (counts.in_review ?? 0) +
    (counts.approved ?? 0) +
    (counts.superseded ?? 0)
  if (total === 0) {
    return ''
  }
  return `${approved}/${total}`
}
